import { ENTITY_PREFIX } from 'app/remote/constants';
import { AppState } from 'app/store';
import {
  IEntityType,
  Entity,
  IIdentifiableEntity,
  EntityId,
  ExtractIEntity,
} from 'app/types/entity';
import { Map } from 'immutable';

export const getAllEntitiesOfType = <T extends IIdentifiableEntity<EntityId>>(
  state: AppState,
  typeObject: IEntityType<T>,
): Map<T['attributes']['id'], Entity<T>> => {
  const allEntities: Map<T['attributes']['id'], Entity<T>> =
    state.getIn([ENTITY_PREFIX, typeObject.storePrefix]) ?? Map();

  // With polymorphic types such as `Section`, the same store prefix contains
  // subtypes as well, so filter out only the type we actually want.
  // Only filter if the type is polymorphic (has `extends` field
  // in type definition). Otherwise would produce a collection
  // identical in content but different referentially every time
  // this funcion is run.
  if (typeObject.extends) {
    const filtered = allEntities.filter((entity) => (
      // The entity is the exact type we are looking for
      entity.schema === typeObject ||
      // Or the entity is a subtype of the type we are looking for (e.g.
      // `typeObject` is `Section` and `entity` is a `DialogSection`)
      entity.schema.extends === typeObject
    ));

    // Return the same reference if filtering doesn't make a
    // difference in the output. This should help when this value
    // is used for invalidating memoized inputs (like in `reselect`
    // selectors or `useMemo`)
    return filtered.size === allEntities.size ? allEntities : filtered;
  } else {
    return allEntities;
  }
};

/**
 * Look for the given entity in the store but return null if it is not found
 * @param state
 * @param typeObject The EntityType of the entity to look for
 * @param id The id of the entity to look for.
 */
export const maybeGetEntityOfType = <T extends IEntityType<any>>(
  state: AppState,
  typeObject: T,
  id: ExtractIEntity<T>['attributes']['id'],
): Entity<ExtractIEntity<T>> | null => state.getIn([ENTITY_PREFIX, typeObject.storePrefix, id]) ?? null;

export const getEntityOfTypeOrThrow = <T extends IEntityType<any>>(
  state: AppState,
  typeObject: T,
  id: ExtractIEntity<T>['attributes']['id'],
): Entity<ExtractIEntity<T>> => {
  const entity = maybeGetEntityOfType(state, typeObject, id);
  if (entity === null) {
    throw new Error(`Could not find ${typeObject.typeName} with id ${id} in the store`);
  }
  return entity;
};
